// Matemática pura de ritmo/projeção de meta comercial — extraída de PerformanceDashboard.tsx
// pra ser consumida também pelo bloco de Meta x Realizado do Analytics Comercial, sem as
// duas telas divergirem na fórmula.

export type Status3 = 'above' | 'inline' | 'below';

export interface RealRow {
    id: number;
    responsavel_id: string | null;
    kwh: number;
    date: string | null;
}

export const getBusinessDays = (start: Date, end: Date): Date[] => {
    const days: Date[] = [];
    const d = new Date(start);
    while (d <= end) {
        if (d.getDay() !== 0 && d.getDay() !== 6) days.push(new Date(d));
        d.setDate(d.getDate() + 1);
    }
    return days;
};

export interface MetaRealizadoInput {
    /** Linhas já filtradas pela visão/canal desejados. */
    viewRows: RealRow[];
    /** Soma das metas dos vendedores visíveis no filtro atual. */
    totalMeta: number;
    /** YYYY-MM-DD. */
    dateStart: string;
    dateEnd: string;
}

export interface MetaRealizadoComputed {
    totalVolume: number;
    metaPct: number;
    gap: number;
    bizRemain: number;
    totalBiz: number;
    bizPassed: number;
    isPeriod: boolean;
    avgDayNeeded: number;
    expectedPerDay: number;
    expectedToDate: number;
    toDateDiff: number;
    toDatePct: number;
    toDateStatus: Status3;
    projecao: number;
    projecaoPct: number;
    projecaoStatus: Status3;
    ritmoDia: number;
    realizedToday: number;
    dailyStatus: Status3;
    toDateChartData: Array<{ day: string; date: string; esperado: number; realizado: number }>;
    weeks: Array<{ week: string; volume: number; metaSemana: number }>;
    metaPerWeek: number;
}

export function computeMetaRealizado({ viewRows, totalMeta, dateStart, dateEnd }: MetaRealizadoInput): MetaRealizadoComputed {
    const totalVolume = viewRows.reduce((a, r) => a + r.kwh, 0);
    const metaPct = totalMeta > 0 ? (totalVolume / totalMeta) * 100 : 0;
    const gap = Math.max(0, totalMeta - totalVolume);

    // Dias úteis
    const start = new Date(dateStart + 'T00:00:00');
    const end = new Date(dateEnd + 'T23:59:59');
    const now = new Date();
    const isPeriod = now >= start && now <= end;
    let bizRemain = 0, totalBiz = 0, bizPassed = 0;
    const dc = new Date(start);
    while (dc <= end) {
        if (dc.getDay() !== 0 && dc.getDay() !== 6) { totalBiz++; if (isPeriod && dc <= now) bizPassed++; }
        dc.setDate(dc.getDate() + 1);
    }
    if (isPeriod) { const c = new Date(now); c.setDate(c.getDate() + 1); while (c <= end) { if (c.getDay() !== 0 && c.getDay() !== 6) bizRemain++; c.setDate(c.getDate() + 1); } }
    const avgDayNeeded = bizRemain > 0 ? gap / bizRemain : 0;

    // Ritmo (to-date)
    const expectedPerDay = totalBiz > 0 ? totalMeta / totalBiz : 0;
    const expectedToDate = expectedPerDay * bizPassed;
    const toDateDiff = totalVolume - expectedToDate;
    const toDatePct = expectedToDate > 0 ? (totalVolume / expectedToDate) * 100 : 0;
    const toDateStatus: Status3 = toDatePct > 105 ? 'above' : toDatePct >= 95 ? 'inline' : 'below';

    // Projeção de fechamento
    const ritmoDia = bizPassed > 0 ? totalVolume / bizPassed : 0;
    const projecao = bizPassed > 0 ? ritmoDia * totalBiz : 0;
    const projecaoPct = totalMeta > 0 ? (projecao / totalMeta) * 100 : 0;
    const projecaoStatus: Status3 = bizPassed === 0 ? 'inline' : projecao >= totalMeta ? 'above' : projecaoPct >= 90 ? 'inline' : 'below';

    // Atingimento diário
    const todayStr = now.toISOString().split('T')[0];
    const realizedToday = viewRows.filter(r => (r.date || '').split('T')[0] === todayStr).reduce((a, r) => a + r.kwh, 0);
    const dailyStatus: Status3 = expectedPerDay > 0
        ? (realizedToday >= expectedPerDay * 1.05 ? 'above' : realizedToday >= expectedPerDay * 0.95 ? 'inline' : 'below')
        : 'inline';

    // To-date chart — acumulado por dia útil
    const bizDays = getBusinessDays(start, isPeriod ? now : end);
    const toDateChartData = bizDays.map((day, idx) => {
        const dayStr = day.toISOString().split('T')[0];
        const dayNum = idx + 1;
        const realizedCum = viewRows.filter(r => (r.date || '').split('T')[0] <= dayStr).reduce((a, r) => a + r.kwh, 0);
        return { day: `D${dayNum}`, date: `${day.getDate()}/${day.getMonth() + 1}`, esperado: Math.round(expectedPerDay * dayNum), realizado: Math.round(realizedCum) };
    });

    // Resultado semanal — invariante: Σ semanas = total
    const weeks: MetaRealizadoComputed['weeks'] = [];
    let totalWeeks = 0;
    { let c = new Date(start); while (c <= end) { const we = new Date(c); we.setDate(we.getDate() + 6); if (we > end) we.setTime(end.getTime()); totalWeeks++; c = new Date(we); c.setDate(c.getDate() + 1); } }
    const metaPerWeek = totalWeeks > 0 ? totalMeta / totalWeeks : 0;
    let wn = 1; let ws = new Date(start);
    while (ws <= end) {
        const we = new Date(ws); we.setDate(we.getDate() + 6); if (we > end) we.setTime(end.getTime());
        const wsStart = new Date(ws.getFullYear(), ws.getMonth(), ws.getDate(), 0, 0, 0);
        const wsEnd = new Date(we.getFullYear(), we.getMonth(), we.getDate(), 23, 59, 59);
        const wVol = viewRows.filter(r => { const d = r.date ? new Date(r.date) : null; return d && d >= wsStart && d <= wsEnd; }).reduce((a, r) => a + r.kwh, 0);
        weeks.push({ week: `Sem ${wn}`, volume: wVol, metaSemana: metaPerWeek });
        wn++; ws = new Date(we); ws.setDate(ws.getDate() + 1);
    }

    return {
        totalVolume, metaPct, gap, bizRemain, totalBiz, bizPassed, isPeriod, avgDayNeeded,
        expectedPerDay, expectedToDate, toDateDiff, toDatePct, toDateStatus,
        projecao, projecaoPct, projecaoStatus, ritmoDia,
        realizedToday, dailyStatus, toDateChartData, weeks, metaPerWeek,
    };
}
